import {Badge, view} from '@sail/ui';
import {FormattedMessage, defineMessages} from 'react-intl';
import {Audience, Payout} from './MerchantPayoutStatus';
import {StatusDescription} from './StatusDescription';

const messages = defineMessages({
  payoutStatusTitle: {
    id: 'mse_inline_data.merchant_payout_status.payout_status_title',
    description: 'Title next to the payout status type label.',
    defaultMessage: 'Payout status',
  },
  statusInTransit: {
    id: 'mse_inline_data.merchant_payout_status.status.in_transit',
    description: 'Payout status: In transit (to bank account)',
    defaultMessage: 'In transit',
  },
  statusPending: {
    id: 'mse_inline_data.merchant_payout_status.status.pending',
    description: 'Payout status: Pending',
    defaultMessage: 'Pending',
  },
  statusPaid: {
    id: 'mse_inline_data.merchant_payout_status.status.paid',
    description: 'Payout status: Paid (to merchant)',
    defaultMessage: 'Paid',
  },
  statusFailed: {
    id: 'mse_inline_data.merchant_payout_status.status.failed',
    description: 'Payout status: Failed',
    defaultMessage: 'Failed',
  },
  statusCanceled: {
    id: 'mse_inline_data.merchant_payout_status.status.canceled',
    description: 'Payout status: Canceled',
    defaultMessage: 'Canceled',
  },
});

const StatusIcon = ({status}: {status: string | undefined}) => {
  // Opus::TransferPayout::Model::Transfer.smart_status
  switch (status) {
    case 'in_transit':
      return (
        <Badge type="info">
          <FormattedMessage {...messages.statusInTransit} />
        </Badge>
      );
    case 'pending':
      return (
        <Badge type="info">
          <FormattedMessage {...messages.statusPending} />
        </Badge>
      );
    case 'paid':
      return (
        <Badge type="positive">
          <FormattedMessage {...messages.statusPaid} />
        </Badge>
      );
    case 'failed':
      return (
        <Badge type="negative">
          <FormattedMessage {...messages.statusFailed} />
        </Badge>
      );
    case 'canceled':
      return (
        <Badge type="negative">
          <FormattedMessage {...messages.statusCanceled} />
        </Badge>
      );
    default:
      return null;
  }
};

export const StatusSection = ({
  payout,
  isFirstPayout,
  isPayoutDateOver5DaysAgo,
  updatePayoutPermissions,
  merchantToken,
  audience,
  showManualPayoutMessage,
  platformName,
}: {
  payout: Payout | null;
  isFirstPayout: boolean | undefined;
  isPayoutDateOver5DaysAgo: boolean | undefined;
  updatePayoutPermissions: boolean | undefined;
  merchantToken?: string;
  audience?: Audience;
  showManualPayoutMessage: boolean | undefined;
  platformName: string | undefined;
}) => {
  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      {payout ? (
        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <view.div
            css={{stack: 'x', gap: 'small', distribute: 'space-between'}}
          >
            <view.div
              data-testid="payout-status-title"
              css={{font: 'label.medium.emphasized'}}
            >
              <FormattedMessage {...messages.payoutStatusTitle} />
            </view.div>
          </view.div>
          <view.div data-testid="payout-status-label">
            <StatusIcon status={payout.status} />
          </view.div>
        </view.div>
      ) : null}
      <StatusDescription
        payout={payout || undefined}
        isFirstPayout={isFirstPayout}
        isPayoutDateOver5DaysAgo={isPayoutDateOver5DaysAgo}
        hasUpdatePayoutPermissions={updatePayoutPermissions}
        merchantToken={merchantToken}
        audience={audience}
        showManualPayoutMessage={showManualPayoutMessage}
        platformName={platformName}
      />
    </view.div>
  );
};
